import React, { useCallback, useEffect, useMemo, useState } from 'react'
import membershipIconSrc from '@assets/images/Icons/Membership.svg'
import {
  Badge,
  Button,
  DataTable,
  EmptyState,
  ToggleSwitch,
  type BadgeColor,
  type BadgeMode,
  type DataTableColumn,
  type DataTableSortDirection,
} from '@enamelai/design-system'
import type { MembershipTier } from '@dataTypes/membership.type'
import { useTheme } from '@context/ThemeContext'
import { useSettingsHeader } from '@pages/Settings/SettingsHeader.context'
import SettingsRowActions from '../../shared/SettingsRowActions.component'
import AddMembershipTier from './AddMembershipTier.component'
import DeleteMembershipConfirmModal from './DeleteMembershipConfirmModal.component'
import {
  formatMembershipPrice,
  type MembershipBenefit,
  type MembershipBenefitVariant,
} from './membershipSettings.constants'
import useMembership from './useMembership'
import './MembershipStyle.css'

type MembershipView = 'list' | 'add' | 'edit'
type MembershipTierRow = { id: string }

const MEMBERSHIP_PAGE_SIZE = 10
const MEMBERSHIP_PAGE_SIZE_OPTIONS = [10, 25, 50, 100]

const MEMBERSHIP_ICON_STYLE = {
  '--membership-icon': `url(${membershipIconSrc})`,
} as React.CSSProperties

const BENEFIT_BADGE_COLOR: Record<MembershipBenefitVariant, BadgeColor> = {
  blue: 'blue',
  indigo: 'indigo',
  green: 'vivid-green',
  red: 'orange',
  gray: 'gray',
}

const compareTierValues = (
  left: string | number,
  right: string | number,
): number => {
  if (typeof left === 'number' && typeof right === 'number') {
    return left - right
  }

  return String(left).localeCompare(String(right), undefined, {
    numeric: true,
    sensitivity: 'base',
  })
}

const getTierSortValue = (
  tier: MembershipTier,
  columnId: string,
): string | number => {
  if (columnId === 'enabled') return tier.enabled ? 1 : 0
  if (columnId === 'monthlyPrice') return tier.monthlyPrice
  if (columnId === 'annualPrice') return tier.annualPrice
  if (columnId === 'benefits') {
    return tier.benefits.map((benefit) => benefit.label).join(' ')
  }
  return tier.name
}

const MembershipBenefitBadge = ({
  benefit,
  mode,
}: {
  benefit: MembershipBenefit
  mode: BadgeMode
}): React.ReactElement => (
  <Badge
    mode={mode}
    color={BENEFIT_BADGE_COLOR[benefit.variant]}
    label={benefit.label}
  />
)

const Membership = (): React.ReactElement => {
  const { setHeaderButtons } = useSettingsHeader()
  const { resolvedTheme } = useTheme()
  const badgeMode: BadgeMode = resolvedTheme === 'dark' ? 'solid' : 'tinted'
  const { tiers, dataReady, saveTier, deleteTier, toggleTierStatus } =
    useMembership()
  const [view, setView] = useState<MembershipView>('list')
  const [editingTierId, setEditingTierId] = useState<string | null>(null)
  const [sortColumnId, setSortColumnId] = useState<string | undefined>('name')
  const [sortDirection, setSortDirection] =
    useState<DataTableSortDirection>('asc')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(MEMBERSHIP_PAGE_SIZE)
  const [tierToDelete, setTierToDelete] = useState<MembershipTier | null>(null)
  const showMembershipEmpty = dataReady && tiers.length === 0

  const editingTier = useMemo(
    () => tiers.find((tier) => tier.id === editingTierId),
    [editingTierId, tiers],
  )

  const openAddTier = useCallback((): void => {
    setEditingTierId(null)
    setView('add')
  }, [])

  const openEditTier = useCallback((tierId: string): void => {
    setEditingTierId(tierId)
    setView('edit')
  }, [])

  const closeForm = useCallback((): void => {
    setView('list')
    setEditingTierId(null)
  }, [])

  const handleSaveTier = useCallback(
    async (
      tier: MembershipTier,
      perks: Parameters<typeof saveTier>[1],
      mode: 'add' | 'edit',
    ): Promise<void> => {
      const saved = await saveTier(tier, perks, mode)
      if (saved) {
        closeForm()
      }
    },
    [closeForm, saveTier],
  )

  const handleToggleStatus = useCallback(
    (tierId: string, enabled: boolean): void => {
      void toggleTierStatus(tierId, enabled)
    },
    [toggleTierStatus],
  )

  const requestDeleteTier = useCallback(
    (tierId: string): void => {
      const tier = tiers.find((item) => item.id === tierId)
      if (tier) setTierToDelete(tier)
    },
    [tiers],
  )

  const confirmDeleteTier = useCallback((): void => {
    if (!tierToDelete) return
    void deleteTier(tierToDelete.id)
    setTierToDelete(null)
  }, [deleteTier, tierToDelete])

  const deleteEditingTier = useCallback(async (): Promise<void> => {
    if (!editingTier) return
    const deleted = await deleteTier(editingTier.id)
    if (deleted) closeForm()
  }, [closeForm, deleteTier, editingTier])

  const handleSort = useCallback((columnId: string): void => {
    setSortColumnId((current) => {
      if (current === columnId) {
        setSortDirection((direction) => (direction === 'asc' ? 'desc' : 'asc'))
        return current
      }

      setSortDirection('asc')
      return columnId
    })
    setCurrentPage(1)
  }, [])

  const handlePageSizeChange = useCallback((nextPageSize: number): void => {
    setPageSize(nextPageSize)
    setCurrentPage(1)
  }, [])

  useEffect(() => {
    if (view !== 'list') return

    setHeaderButtons(
      <div className="settings-membership-header-actions">
        {!showMembershipEmpty ? (
          <Button variant="primary" size="md" onClick={openAddTier}>
            Add tier
          </Button>
        ) : null}
      </div>,
    )

    return () => setHeaderButtons(null)
  }, [openAddTier, setHeaderButtons, showMembershipEmpty, view])

  const sortedTiers = useMemo(() => {
    if (!sortColumnId) return tiers

    const direction = sortDirection === 'desc' ? -1 : 1
    return [...tiers].sort(
      (left, right) =>
        compareTierValues(
          getTierSortValue(left, sortColumnId),
          getTierSortValue(right, sortColumnId),
        ) * direction,
    )
  }, [sortColumnId, sortDirection, tiers])

  const totalPages = Math.max(1, Math.ceil(sortedTiers.length / pageSize))
  const safePage = Math.min(currentPage, totalPages)
  const pagedTiers = useMemo(() => {
    const start = (safePage - 1) * pageSize
    return sortedTiers.slice(start, start + pageSize)
  }, [pageSize, safePage, sortedTiers])

  const tierById = useMemo(
    () => new Map(pagedTiers.map((tier) => [tier.id, tier])),
    [pagedTiers],
  )

  const columns = useMemo<DataTableColumn<MembershipTierRow>[]>(
    () => [
      {
        id: 'enabled',
        label: 'Status',
        sortable: true,
        width: 80,
        renderCell: (row) => {
          const tier = tierById.get(row.id)
          if (!tier) return null
          return (
            <ToggleSwitch
              checked={tier.enabled}
              aria-label={`${tier.name} status`}
              onChange={(checked) => handleToggleStatus(tier.id, checked)}
            />
          )
        },
      },
      {
        id: 'name',
        label: 'Membership tier',
        sortable: true,
        width: 160,
        renderCell: (row) => {
          const tier = tierById.get(row.id)
          if (!tier) return null
          return (
            <span className="settings-membership__tier">
              <span
                className={`settings-membership__tier-icon settings-membership__tier-icon--${tier.shieldVariant}`}
                style={MEMBERSHIP_ICON_STYLE}
                aria-hidden
              />
              <span className="settings-membership__tier-name">
                {tier.name}
              </span>
            </span>
          )
        },
      },
      {
        id: 'monthlyPrice',
        label: 'Monthly pricing',
        sortable: true,
        width: 144,
        renderCell: (row) => {
          const tier = tierById.get(row.id)
          return tier ? formatMembershipPrice(tier.monthlyPrice) : null
        },
      },
      {
        id: 'annualPrice',
        label: 'Annual pricing',
        sortable: true,
        width: 144,
        renderCell: (row) => {
          const tier = tierById.get(row.id)
          return tier ? formatMembershipPrice(tier.annualPrice) : null
        },
      },
      {
        id: 'benefits',
        label: 'Tier benefits',
        sortable: true,
        renderCell: (row) => {
          const tier = tierById.get(row.id)
          if (!tier) return null
          return (
            <div className="settings-membership__benefits">
              {tier.benefits.map((benefit) => (
                <MembershipBenefitBadge
                  key={benefit.label}
                  benefit={benefit}
                  mode={badgeMode}
                />
              ))}
              {tier.extraBenefitsCount ? (
                <Badge
                  mode={badgeMode}
                  color="gray"
                  label={`+${tier.extraBenefitsCount} more`}
                />
              ) : null}
            </div>
          )
        },
      },
    ],
    [badgeMode, handleToggleStatus, tierById],
  )

  const rows = useMemo<MembershipTierRow[]>(
    () => pagedTiers.map((tier) => ({ id: tier.id })),
    [pagedTiers],
  )

  if (view === 'add') {
    return (
      <AddMembershipTier
        mode="add"
        onCancel={closeForm}
        onSave={(tier, perks) => handleSaveTier(tier, perks, 'add')}
      />
    )
  }

  if (view === 'edit' && editingTier) {
    return (
      <AddMembershipTier
        mode="edit"
        initialTier={editingTier}
        onCancel={closeForm}
        onSave={(tier, perks) => handleSaveTier(tier, perks, 'edit')}
        onDelete={deleteEditingTier}
      />
    )
  }

  return (
    <div className="settings-membership">
      {dataReady ? (
        showMembershipEmpty ? (
          <EmptyState
            className="settings-membership-empty"
            iconClass="mgc mgc_shield_fill"
            title="No membership tiers found"
            description="You don't have any membership tiers on record yet."
            actionLabel="Add tier"
            onAction={openAddTier}
          />
        ) : (
          <DataTable
            className="settings-membership__table"
            columns={columns}
            rows={rows}
            sortColumnId={sortColumnId}
            sortDirection={sortDirection}
            onSort={handleSort}
            actionsLabel="Actions"
            renderActions={(row) => (
              <SettingsRowActions
                onEdit={() => openEditTier(row.id)}
                onDelete={() => requestDeleteTier(row.id)}
              />
            )}
            pagination={{
              currentPage: safePage,
              totalPages,
              pageSize,
              pageSizeOptions: MEMBERSHIP_PAGE_SIZE_OPTIONS,
              onPageChange: setCurrentPage,
              onPageSizeChange: handlePageSizeChange,
            }}
          />
        )
      ) : null}
      <DeleteMembershipConfirmModal
        open={Boolean(tierToDelete)}
        title="Delete membership tier"
        message={
          tierToDelete
            ? `Are you sure you want to delete “${tierToDelete.name}”?`
            : ''
        }
        onClose={() => setTierToDelete(null)}
        onConfirm={confirmDeleteTier}
      />
    </div>
  )
}

export default Membership
